import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { employeesApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Plus,
  Search,
  Users,
  Mail,
  Phone,
  Send,
  Pencil,
  Trash2,
  UserCheck,
  UserX,
  Building2,
  CreditCard,
  Bitcoin,
  Key,
  UserPlus,
  Shield,
  Check,
  RotateCw,
  Clock,
} from "lucide-react";

const positions = [
  { value: "project_manager", label: "Project Manager" },
  { value: "sales", label: "Sales Manager" },
  { value: "finance_director", label: "Finance Director" },
  { value: "art_director", label: "Art Director" },
  { value: "3d_artist", label: "3D Artist" },
  { value: "animator", label: "Animator" },
  { value: "designer", label: "Designer" },
  { value: "developer", label: "Developer" },
  { value: "admin", label: "Administrator" },
  { value: "other", label: "Other" },
];

// Org-structure departments — must mirror DepartmentType in backend/server.py
export const departments = [
  { value: "management", label: "Management" },
  { value: "production", label: "Production (3D / Animation)" },
  { value: "art_direction", label: "Art Direction" },
  { value: "project_management", label: "Project Management" },
  { value: "sales", label: "Sales" },
  { value: "marketing", label: "Marketing" },
  { value: "support", label: "Support" },
  { value: "finance", label: "Finance" },
  { value: "hr", label: "HR" },
  { value: "development", label: "Development" },
];

const StaffPage = () => {
  const { formatAmount, convertAmount, displayCurrency, getCurrencySymbol } = useCurrency();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [positionFilter, setPositionFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [paymentMethodTab, setPaymentMethodTab] = useState("fop");
  // Portal account state
  const [hasPortalAccount, setHasPortalAccount] = useState(false);
  const [existingAccountInfo, setExistingAccountInfo] = useState(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [accountRole, setAccountRole] = useState("team");
  // Password reset state
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [togglingRoleplay, setTogglingRoleplay] = useState(false);
  const [togglingPortfolio, setTogglingPortfolio] = useState(false);
  // Email log state
  const [emailLogUserId, setEmailLogUserId] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [resendingInvite, setResendingInvite] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    position: "project_manager",
    title: "",
    department: "",
    manager_id: "",
    email: "",
    phone: "",
    telegram: "",
    salary: 0,
    currency: "USD",
    hire_date: "",
    raise_date: "",
    raise_amount: "",
    notes: "",
    is_partnership: false,
    ipn: "",
    crm_bonus_percent: 0,
    art_director_bonus_percent: 0,
    pm_profit_bonus_percent: null,
    sales_bonus_percent: null,
    bonus: 0,
    is_available_for_artist_work: false,
    payment_methods: {
      preferred_method: "fop",
      fop: { iban: "", company_name: "", bank_name: "", edrpou: "" },
      card: { card_number: "", bank: "monobank", recipient_name: "", recipient_iban: "", recipient_ipn: "" },
      crypto: { wallet_address: "", network: "TRC20" }
    },
    // Login credentials
    create_login: false,
    password: ""
  });

  useEffect(() => {
    fetchEmployees();
  }, [showInactive]);

  // Department catalog is data-driven (db.departments); fall back to the static list.
  const [deptList, setDeptList] = useState(departments);
  useEffect(() => {
    employeesApi.listDepartments()
      .then((res) => { if (res.data?.length) setDeptList(res.data.map((d) => ({ value: d.id, label: d.label }))); })
      .catch(() => {});
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await employeesApi.getAll(showInactive ? undefined : true);
      setEmployees(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvite = async (userId, email) => {
    setResendingInvite(userId);
    try {
      await employeesApi.resendInvite(userId);
      toast.success(`Invite resent to ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend invite");
    } finally {
      setResendingInvite(null);
    }
  };

  const handleShowEmailLog = async (userId) => {
    if (emailLogUserId === userId) {
      setEmailLogUserId(null);
      return;
    }
    setEmailLogUserId(userId);
    try {
      const res = await employeesApi.getEmailLogs(userId);
      setEmailLogs(res.data || []);
    } catch {
      setEmailLogs([]);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        // Empty raise amount → null (backend expects float|null, "" breaks coercion)
        raise_amount:
          formData.raise_amount === "" || formData.raise_amount === null
            ? null
            : parseFloat(formData.raise_amount),
        raise_date: formData.raise_date || null,
        // Org-structure fields: "" from empty selects → null (department is an enum,
        // manager_id "" means "no manager", title "" means unset)
        title: formData.title || null,
        department: formData.department || null,
        manager_id: formData.manager_id || null,
        payment_methods: {
          ...formData.payment_methods,
          preferred_method: paymentMethodTab
        }
      };
      // Remove login fields from employee data - handled separately
      delete dataToSend.create_login;
      delete dataToSend.password;
      
      if (editingEmployee) {
        await employeesApi.update(editingEmployee.id, dataToSend);
        toast.success("Employee updated");
      } else {
        // Create employee
        const createResponse = await employeesApi.create(dataToSend);
        const newEmployeeId = createResponse.data.id;
        toast.success("Employee created");
        
        // If create_login is checked and password provided, create portal account
        if (formData.create_login && formData.password && formData.password.length >= 6) {
          try {
            await employeesApi.createAccount(newEmployeeId, {
              password: formData.password,
              role: accountRole
            });
            toast.success("Portal account created");
          } catch (accError) {
            console.error("Error creating portal account:", accError);
            toast.error("Employee created, but portal account failed");
          }
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      // Handle validation errors from Pydantic
      const detail = error.response?.data?.detail;
      let errorMessage = "Failed to save employee";
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors are arrays
        errorMessage = detail.map(e => e.msg || e.message || String(e)).join(", ");
      } else if (detail && typeof detail === "object") {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    }
  };

  const handleEdit = async (employee) => {
    setEditingEmployee(employee);
    const pm = employee.payment_methods || {};
    setPaymentMethodTab(pm.preferred_method || "fop");
    setFormData({
      name: employee.name,
      position: employee.position,
      title: employee.title || "",
      department: employee.department || "",
      manager_id: employee.manager_id || "",
      email: employee.email,
      phone: employee.phone || "",
      telegram: employee.telegram || "",
      salary: employee.salary,
      currency: employee.currency,
      hire_date: employee.hire_date || "",
      raise_date: employee.raise_date || "",
      raise_amount: employee.raise_amount ?? "",
      notes: employee.notes || "",
      is_partnership: employee.is_partnership || false,
      ipn: employee.ipn || "",
      crm_bonus_percent: employee.crm_bonus_percent || 0,
      art_director_bonus_percent: employee.art_director_bonus_percent ?? 0,
      pm_profit_bonus_percent: employee.pm_profit_bonus_percent ?? null,
      sales_bonus_percent: employee.sales_bonus_percent ?? null,
      bonus: employee.bonus || 0,
      is_available_for_artist_work: employee.is_available_for_artist_work || false,
      payment_methods: {
        preferred_method: pm.preferred_method || "fop",
        fop: pm.fop || { iban: "", company_name: "", bank_name: "", edrpou: "" },
        card: pm.card || { card_number: "", bank: "monobank", recipient_name: "", recipient_iban: "", recipient_ipn: "" },
        crypto: pm.crypto || { wallet_address: "", network: "TRC20" }
      }
    });
    // Reset account creation state
    setAccountPassword("");
    setAccountRole("team");
    setHasPortalAccount(false);
    setExistingAccountInfo(null);
    // Check if employee has a portal account
    try {
      const response = await employeesApi.hasAccount(employee.id);
      setHasPortalAccount(response.data.has_account);
      if (response.data.user) {
        setExistingAccountInfo(response.data.user);
      }
    } catch (error) {
      console.error("Error checking account status:", error);
    }
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await employeesApi.delete(id);
      toast.success("Employee deactivated");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to deactivate employee");
    }
  };

  const handleCreateAccount = async () => {
    if (!editingEmployee) return;
    setCreatingAccount(true);
    try {
      const response = await employeesApi.createAccount(editingEmployee.id, {
        role: accountRole
      });
      toast.success(response.data.invite_sent 
        ? "Portal access granted and invite email sent!" 
        : "Portal account created successfully");
      setHasPortalAccount(true);
      setExistingAccountInfo(response.data.user);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to create account");
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editingEmployee) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResettingPassword(true);
    try {
      await employeesApi.resetPassword(editingEmployee.id, { new_password: newPassword });
      toast.success("Password reset successfully");
      setNewPassword("");
      setShowResetPassword(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleUpdateRole = async (newRole) => {
    if (!editingEmployee) return;
    setUpdatingRole(true);
    try {
      await employeesApi.updateRole(editingEmployee.id, { role: newRole });
      toast.success("Role updated successfully");
      setExistingAccountInfo({ ...existingAccountInfo, role: newRole });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to update role");
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleToggleRoleplay = async (enabled) => {
    if (!editingEmployee) return;
    setTogglingRoleplay(true);
    try {
      await employeesApi.setRoleplayAccess(editingEmployee.id, { enabled });
      toast.success(enabled ? "Roleplay access enabled" : "Roleplay access disabled");
      setExistingAccountInfo({ ...existingAccountInfo, roleplay_access: enabled });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to update roleplay access");
    } finally {
      setTogglingRoleplay(false);
    }
  };

  const handleTogglePortfolio = async (enabled) => {
    if (!editingEmployee) return;
    setTogglingPortfolio(true);
    try {
      await employeesApi.setPortfolioAccess(editingEmployee.id, { enabled });
      toast.success(enabled ? "Portfolio access enabled" : "Portfolio access disabled");
      setExistingAccountInfo({ ...existingAccountInfo, portfolio_access: enabled });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to update portfolio access");
    } finally {
      setTogglingPortfolio(false);
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setPaymentMethodTab("fop");
    setAccountPassword("");
    setAccountRole("team");
    setHasPortalAccount(false);
    setExistingAccountInfo(null);
    setShowResetPassword(false);
    setNewPassword("");
    setFormData({
      name: "",
      position: "project_manager",
      email: "",
      phone: "",
      telegram: "",
      salary: 0,
      currency: "USD",
      hire_date: "",
      raise_date: "",
      raise_amount: "",
      notes: "",
      is_partnership: false,
      ipn: "",
      crm_bonus_percent: 0,
      pm_profit_bonus_percent: null,
      sales_bonus_percent: null,
      bonus: 0,
      payment_methods: {
        preferred_method: "fop",
        fop: { iban: "", company_name: "", bank_name: "", edrpou: "" },
        card: { card_number: "", bank: "monobank", recipient_name: "", recipient_iban: "", recipient_ipn: "" },
        crypto: { wallet_address: "", network: "TRC20" }
      }
    });
  };

  const getPositionLabel = (value) => positions.find(p => p.value === value)?.label || value;

  const getPositionColor = (position) => {
    const colors = {
      project_manager: "bg-sky-500/10 text-sky-400 border-sky-500/20",
      art_director: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      admin: "bg-red-500/10 text-red-400 border-red-500/20",
      sales: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    };
    return colors[position] || "bg-zinc-800 text-zinc-400 border-zinc-700";
  };

  const getPaymentIcon = (method) => {
    if (method === "fop") return <Building2 className="h-4 w-4 text-sky-400" />;
    if (method === "card") return <CreditCard className="h-4 w-4 text-green-400" />;
    if (method === "crypto") return <Bitcoin className="h-4 w-4 text-orange-400" />;
    return null;
  };

  const filteredEmployees = employees.filter((e) => {
    const matchesSearch = e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPosition = positionFilter === "all" || e.position === positionFilter;
    const matchesRole = roleFilter === "all" || e.portal_role === roleFilter;
    return matchesSearch && matchesPosition && matchesRole;
  });

  // Calculate total salary converted to display currency
  const totalSalary = employees.filter(e => e.is_active).reduce((sum, e) => {
    const converted = convertAmount(e.salary || 0, e.currency || "UAH");
    return sum + converted;
  }, 0);
  const activeCount = employees.filter(e => e.is_active).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Staff</h1>
          <p className="text-sm text-zinc-500">Company staff management</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-employee-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-zinc-50 font-['Manrope']">
                {editingEmployee ? "Edit Employee" : "New Employee"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Full Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Position</Label>
                  <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {positions.map((p) => (
                        <SelectItem key={p.value} value={p.value} className="text-zinc-300">{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Job Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    placeholder="e.g. Head of 3D"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Department</Label>
                  <Select value={formData.department || "none"} onValueChange={(v) => setFormData({ ...formData, department: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue placeholder="— none —" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none" className="text-zinc-400">— none —</SelectItem>
                      {deptList.map((d) => (
                        <SelectItem key={d.value} value={d.value} className="text-zinc-300">{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Manager</Label>
                  <Select value={formData.manager_id || "none"} onValueChange={(v) => setFormData({ ...formData, manager_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue placeholder="— none —" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="none" className="text-zinc-400">— none —</SelectItem>
                      {employees
                        .filter((emp) => emp.id !== editingEmployee?.id)
                        .map((emp) => (
                          <SelectItem key={emp.id} value={emp.id} className="text-zinc-300">{emp.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Telegram</Label>
                  <Input
                    value={formData.telegram}
                    onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    placeholder="@username"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Salary</Label>
                  <Input
                    type="number"
                    value={formData.salary}
                    onChange={(e) => setFormData({ ...formData, salary: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="USD" className="text-zinc-300">USD</SelectItem>
                      <SelectItem value="EUR" className="text-zinc-300">EUR</SelectItem>
                      <SelectItem value="UAH" className="text-zinc-300">UAH</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Hire Date</Label>
                  <Input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
              </div>

              {/* Latest salary raise — date + amount (Дата/Сума підвищення) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Last raise date</Label>
                  <Input
                    type="date"
                    value={formData.raise_date}
                    onChange={(e) => setFormData({ ...formData, raise_date: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Raise amount</Label>
                  <Input
                    type="number"
                    value={formData.raise_amount}
                    onChange={(e) => setFormData({ ...formData, raise_amount: e.target.value })}
                    placeholder="e.g. 500"
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
              </div>

              {/* Tax ID (bonus fields moved to /staff/bonus-matrix) */}
              <div className="space-y-2">
                <Label className="text-zinc-300">ІПН (Tax ID)</Label>
                <Input
                  value={formData.ipn}
                  onChange={(e) => setFormData({ ...formData, ipn: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-xs"
                  placeholder="1234567890"
                />
              </div>
              <div className="rounded-md bg-zinc-900/40 border border-zinc-800 px-3 py-2 text-xs text-zinc-400">
                Bonus settings (CRM %, Sales %, PM %, AD %, fixed bonus, partnership) — edit in{" "}
                <Link to="/staff/bonus-matrix" className="text-orange-400 hover:text-orange-300">
                  Bonus Matrix
                </Link>.
              </div>

              {/* Art Director or Other position can take artist work */}
              {(formData.position === "art_director" || formData.position === "other") && (
                <div className="flex items-center space-x-2 pt-2 col-span-2">
                  <input
                    type="checkbox"
                    id="is_available_for_artist_work"
                    checked={formData.is_available_for_artist_work}
                    onChange={(e) => setFormData({ ...formData, is_available_for_artist_work: e.target.checked })}
                    className="h-4 w-4 rounded border-zinc-700 bg-zinc-900 text-orange-500 focus:ring-orange-500"
                  />
                  <Label htmlFor="is_available_for_artist_work" className="text-zinc-300 text-sm cursor-pointer">
                    Може брати проектну роботу як артист (показувати у списку артистів)
                  </Label>
                </div>
              )}

              {/* Payment Methods */}
              <div className="pt-4 border-t border-zinc-800">
                <Label className="text-zinc-300 text-sm font-medium">Payment Method</Label>
                <Tabs value={paymentMethodTab} onValueChange={setPaymentMethodTab} className="mt-2">
                  <TabsList className="bg-zinc-800 border border-zinc-700">
                    <TabsTrigger value="fop" className="data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400">
                      <Building2 className="h-4 w-4 mr-1" /> ФОП
                    </TabsTrigger>
                    <TabsTrigger value="card" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                      <CreditCard className="h-4 w-4 mr-1" /> Карта
                    </TabsTrigger>
                    <TabsTrigger value="crypto" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
                      <Bitcoin className="h-4 w-4 mr-1" /> Крипто
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="fop" className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-500">IBAN</Label>
                        <Input
                          value={formData.payment_methods.fop.iban}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_methods: { ...formData.payment_methods, fop: { ...formData.payment_methods.fop, iban: e.target.value } }
                          })}
                          placeholder="UA91..."
                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-500">Код ЄДРПОУ</Label>
                        <Input
                          value={formData.payment_methods.fop.edrpou}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_methods: { ...formData.payment_methods, fop: { ...formData.payment_methods.fop, edrpou: e.target.value } }
                          })}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-500">Назва компанії</Label>
                      <Input
                        value={formData.payment_methods.fop.company_name}
                        onChange={(e) => setFormData({
                          ...formData,
                          payment_methods: { ...formData.payment_methods, fop: { ...formData.payment_methods.fop, company_name: e.target.value } }
                        })}
                        placeholder="ФОП Прізвище Ім'я По батькові"
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-500">Назва банку</Label>
                      <Input
                        value={formData.payment_methods.fop.bank_name}
                        onChange={(e) => setFormData({
                          ...formData,
                          payment_methods: { ...formData.payment_methods, fop: { ...formData.payment_methods.fop, bank_name: e.target.value } }
                        })}
                        placeholder='АТ "ПУМБ"'
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="card" className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-500">Номер карти</Label>
                        <Input
                          value={formData.payment_methods.card.card_number}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_methods: { ...formData.payment_methods, card: { ...formData.payment_methods.card, card_number: e.target.value } }
                          })}
                          placeholder="5375 4141 XXXX XXXX"
                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-500">Банк</Label>
                        <Select
                          value={formData.payment_methods.card.bank}
                          onValueChange={(v) => setFormData({
                            ...formData,
                            payment_methods: { ...formData.payment_methods, card: { ...formData.payment_methods.card, bank: v } }
                          })}
                        >
                          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="monobank" className="text-zinc-300">Monobank</SelectItem>
                            <SelectItem value="privatbank" className="text-zinc-300">ПриватБанк</SelectItem>
                            <SelectItem value="other" className="text-zinc-300">Інший</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-500">Отримувач</Label>
                      <Input
                        value={formData.payment_methods.card.recipient_name}
                        onChange={(e) => setFormData({
                          ...formData,
                          payment_methods: { ...formData.payment_methods, card: { ...formData.payment_methods.card, recipient_name: e.target.value } }
                        })}
                        placeholder="Прізвище Ім'я По батькові"
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-500">IBAN</Label>
                        <Input
                          value={formData.payment_methods.card.recipient_iban}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_methods: { ...formData.payment_methods, card: { ...formData.payment_methods.card, recipient_iban: e.target.value } }
                          })}
                          placeholder="UA85..."
                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-500">ІПН/ЄДРПОУ</Label>
                        <Input
                          value={formData.payment_methods.card.recipient_ipn}
                          onChange={(e) => setFormData({
                            ...formData,
                            payment_methods: { ...formData.payment_methods, card: { ...formData.payment_methods.card, recipient_ipn: e.target.value } }
                          })}
                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                        />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="crypto" className="space-y-3 mt-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-500">Адреса гаманця</Label>
                      <Input
                        value={formData.payment_methods.crypto.wallet_address}
                        onChange={(e) => setFormData({
                          ...formData,
                          payment_methods: { ...formData.payment_methods, crypto: { ...formData.payment_methods.crypto, wallet_address: e.target.value } }
                        })}
                        placeholder="TRC20 / ERC20 адреса"
                        className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm font-mono"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-zinc-500">Мережа</Label>
                      <Select
                        value={formData.payment_methods.crypto.network}
                        onValueChange={(v) => setFormData({
                          ...formData,
                          payment_methods: { ...formData.payment_methods, crypto: { ...formData.payment_methods.crypto, network: v } }
                        })}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="TRC20" className="text-zinc-300">TRC20 (Tron)</SelectItem>
                          <SelectItem value="ERC20" className="text-zinc-300">ERC20 (Ethereum)</SelectItem>
                          <SelectItem value="BEP20" className="text-zinc-300">BEP20 (BSC)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[60px]"
                />
              </div>

              {/* Portal Account Section */}
              <div className="pt-4 border-t border-zinc-800">
                <Label className="text-zinc-300 text-sm font-medium flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Portal Access
                </Label>
                
                {editingEmployee ? (
                  // Editing existing employee
                  hasPortalAccount ? (
                    <div className="mt-3 p-3 rounded-lg bg-green-900/20 border border-green-800/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-green-400">
                          <Check className="h-4 w-4" />
                          <span className="text-sm font-medium">Account exists</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowResetPassword(!showResetPassword)}
                          className="text-xs text-zinc-400 hover:text-zinc-50 h-7"
                        >
                          {showResetPassword ? "Hide" : "Manage"}
                        </Button>
                      </div>
                      {existingAccountInfo && (
                        <div className="mt-2 text-xs text-zinc-400 space-y-2">
                          <div>Email: {existingAccountInfo.email}</div>
                          <div className="flex items-center gap-2">
                            <span>Role:</span>
                            <Select 
                              value={existingAccountInfo.role} 
                              onValueChange={handleUpdateRole}
                              disabled={updatingRole}
                            >
                              <SelectTrigger className="h-7 w-32 bg-zinc-800 border-zinc-700 text-zinc-50 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800">
                                <SelectItem value="team" className="text-zinc-300 text-xs">Team</SelectItem>
                                <SelectItem value="pm" className="text-zinc-300 text-xs">PM Manager</SelectItem>
                                <SelectItem value="finance" className="text-zinc-300 text-xs">Finance</SelectItem>
                                <SelectItem value="sales" className="text-zinc-300 text-xs">Sales</SelectItem>
                                <SelectItem value="pm_lead" className="text-zinc-300 text-xs">PM Lead</SelectItem>
                                <SelectItem value="sales_director" className="text-zinc-300 text-xs">Sales Director</SelectItem>
                                <SelectItem value="crm_manager" className="text-zinc-300 text-xs">CRM Manager</SelectItem>
                                <SelectItem value="art_director" className="text-zinc-300 text-xs">Art Director</SelectItem>
                                <SelectItem value="artist" className="text-zinc-300 text-xs">Artist</SelectItem>
                                <SelectItem value="partner" className="text-zinc-300 text-xs">Partner</SelectItem>
                                <SelectItem value="client" className="text-zinc-300 text-xs">Client</SelectItem>
                                <SelectItem value="roleplay_tester" className="text-zinc-300 text-xs">Roleplay Tester</SelectItem>
                                <SelectItem value="admin" className="text-zinc-300 text-xs">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Additive Roleplay access — only meaningful for roles
                              that don't already have the sales trainer. */}
                          {!["admin", "sales", "sales_director", "crm_manager", "pm_lead", "roleplay_tester"].includes(existingAccountInfo.role) && (
                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                checked={!!existingAccountInfo.roleplay_access}
                                disabled={togglingRoleplay}
                                onChange={(e) => handleToggleRoleplay(e.target.checked)}
                                className="rounded border-zinc-600 bg-zinc-800 text-indigo-500 focus:ring-indigo-500"
                                data-testid="roleplay-access-toggle"
                              />
                              <span>Roleplay trainer access</span>
                            </label>
                          )}
                          {/* Additive Portfolio access — for roles the portfolio
                              section isn't open to. */}
                          {!["admin", "pm_lead", "pm", "art_director", "sales", "sales_director", "crm_manager", "client"].includes(existingAccountInfo.role) && (
                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                checked={!!existingAccountInfo.portfolio_access}
                                disabled={togglingPortfolio}
                                onChange={(e) => handleTogglePortfolio(e.target.checked)}
                                className="rounded border-zinc-600 bg-zinc-800 text-indigo-500 focus:ring-indigo-500"
                                data-testid="portfolio-access-toggle"
                              />
                              <span>Portfolio access</span>
                            </label>
                          )}
                        </div>
                      )}
                      {showResetPassword && (
                        <div className="mt-3 pt-3 border-t border-green-800/30 space-y-2">
                          <Label className="text-xs text-zinc-400">Reset Password</Label>
                          <div className="flex gap-2">
                            <Input
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="New password (min 6 chars)"
                              className="bg-zinc-950 border-zinc-700 text-zinc-50 text-xs h-8 flex-1"
                              data-testid="reset-password-input"
                            />
                            <Button
                              type="button"
                              onClick={handleResetPassword}
                              disabled={resettingPassword || newPassword.length < 6}
                              className="bg-orange-600 hover:bg-orange-700 text-white h-8 text-xs px-3"
                              data-testid="reset-password-btn"
                            >
                              {resettingPassword ? "..." : "Reset"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                      <p className="text-xs text-zinc-400 mb-3">
                        Create a portal account. An invite email will be sent to set password.
                      </p>
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-500">Role</Label>
                          <Select value={accountRole} onValueChange={setAccountRole}>
                            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="team" className="text-zinc-300">Team</SelectItem>
                              <SelectItem value="pm" className="text-zinc-300">PM Manager</SelectItem>
                              <SelectItem value="finance" className="text-zinc-300">Finance</SelectItem>
                              <SelectItem value="sales" className="text-zinc-300">Sales</SelectItem>
                              <SelectItem value="pm_lead" className="text-zinc-300">PM Lead</SelectItem>
                              <SelectItem value="sales_director" className="text-zinc-300">Sales Director</SelectItem>
                              <SelectItem value="crm_manager" className="text-zinc-300">CRM Manager</SelectItem>
                              <SelectItem value="art_director" className="text-zinc-300">Art Director</SelectItem>
                              <SelectItem value="artist" className="text-zinc-300">Artist</SelectItem>
                              <SelectItem value="roleplay_tester" className="text-zinc-300">Roleplay Tester</SelectItem>
                              <SelectItem value="admin" className="text-zinc-300">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          type="button"
                          onClick={handleCreateAccount}
                          disabled={creatingAccount}
                          className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                          data-testid="create-portal-account-btn"
                        >
                          {creatingAccount ? (
                            <span className="flex items-center gap-2">
                              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                              Creating...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <UserPlus className="h-4 w-4" />
                              Create Account & Send Invite
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                  )
                ) : (
                  // Creating new employee - option to create portal account
                  <div className="mt-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <div className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        id="create_login"
                        checked={formData.create_login}
                        onChange={(e) => setFormData({ ...formData, create_login: e.target.checked })}
                        className="rounded border-zinc-600 bg-zinc-800 text-sky-500 focus:ring-sky-500"
                      />
                      <Label htmlFor="create_login" className="text-xs text-zinc-400 cursor-pointer">
                        Create portal account for this employee
                      </Label>
                    </div>
                    {formData.create_login && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Password</Label>
                            <Input
                              type="password"
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                              placeholder="Min 6 characters"
                              className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-zinc-500">Portal Role</Label>
                            <Select value={accountRole} onValueChange={setAccountRole}>
                              <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800">
                                <SelectItem value="team" className="text-zinc-300">Team</SelectItem>
                                <SelectItem value="pm" className="text-zinc-300">PM Manager</SelectItem>
                                <SelectItem value="finance" className="text-zinc-300">Finance</SelectItem>
                                <SelectItem value="sales" className="text-zinc-300">Sales</SelectItem>
                                <SelectItem value="pm_lead" className="text-zinc-300">PM Lead</SelectItem>
                                <SelectItem value="sales_director" className="text-zinc-300">Sales Director</SelectItem>
                                <SelectItem value="crm_manager" className="text-zinc-300">CRM Manager</SelectItem>
                                <SelectItem value="art_director" className="text-zinc-300">Art Director</SelectItem>
                                <SelectItem value="artist" className="text-zinc-300">Artist</SelectItem>
                                <SelectItem value="admin" className="text-zinc-300">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {formData.password && formData.password.length < 6 && (
                          <p className="text-xs text-amber-400">Password must be at least 6 characters</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                  {editingEmployee ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase font-mono">Employees</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase font-mono">Monthly Payroll</p>
            <p className="text-2xl font-bold text-orange-400 mt-1">
              {getCurrencySymbol()}{totalSalary.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase font-mono">Avg Salary</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1">
              {getCurrencySymbol()}{activeCount > 0 ? Math.round(totalSalary / activeCount).toLocaleString() : 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50 h-9"
          />
        </div>
        <Select value={positionFilter} onValueChange={setPositionFilter}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Position" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Positions</SelectItem>
            {positions.map((p) => (
              <SelectItem key={p.value} value={p.value} className="text-zinc-300">{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Roles</SelectItem>
            <SelectItem value="admin" className="text-zinc-300">Admin</SelectItem>
            <SelectItem value="pm_lead" className="text-zinc-300">PM Lead</SelectItem>
            <SelectItem value="pm" className="text-zinc-300">PM Manager</SelectItem>
            <SelectItem value="team" className="text-zinc-300">Team</SelectItem>
            <SelectItem value="art_director" className="text-zinc-300">Art Director</SelectItem>
            <SelectItem value="sales" className="text-zinc-300">Sales</SelectItem>
            <SelectItem value="sales_director" className="text-zinc-300">Sales Director</SelectItem>
            <SelectItem value="crm_manager" className="text-zinc-300">CRM Manager</SelectItem>
            <SelectItem value="artist" className="text-zinc-300">Artist</SelectItem>
            <SelectItem value="partner" className="text-zinc-300">Partner</SelectItem>
            <SelectItem value="client" className="text-zinc-300">Client</SelectItem>
            <SelectItem value="finance" className="text-zinc-300">Finance</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={showInactive ? "default" : "outline"}
          onClick={() => setShowInactive(!showInactive)}
          className={showInactive ? "bg-zinc-700" : "border-zinc-700 text-zinc-400"}
          size="sm"
        >
          {showInactive ? "All" : "Active"}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400 font-medium w-12">#</TableHead>
              <TableHead className="text-zinc-400 font-medium">Employee</TableHead>
              <TableHead className="text-zinc-400 font-medium">Position</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Portal</TableHead>
              <TableHead className="text-zinc-400 font-medium">Contact</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Payment</TableHead>
              <TableHead className="text-zinc-400 font-medium text-right">Salary</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmployees.map((emp, index) => (
              <React.Fragment key={emp.id}>
              <TableRow className={cn("border-zinc-800 hover:bg-zinc-800/50", !emp.is_active && "opacity-50")}>
                <TableCell className="text-zinc-500 font-mono text-sm">{index + 1}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                      <span className="text-sm text-zinc-300">{emp.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-zinc-50 font-medium text-sm">{emp.name}</p>
                      {emp.hire_date && <p className="text-xs text-zinc-500">Since {emp.hire_date}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={cn("text-xs", getPositionColor(emp.position))}>
                      {getPositionLabel(emp.position)}
                    </Badge>
                    {emp.is_partnership && (
                      <Badge className="bg-purple-900/30 text-purple-400 border-purple-800 text-xs">Partnership</Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {emp.has_portal_access ? (
                    <div className="flex items-center justify-center gap-1">
                      <Key className="h-4 w-4 text-green-400" />
                      <span className="text-xs text-green-400">{emp.portal_role}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-zinc-500 hover:text-orange-400"
                        onClick={(e) => { e.stopPropagation(); handleResendInvite(emp.id, emp.email); }}
                        disabled={resendingInvite === emp.id}
                        title="Resend invite"
                        data-testid={`resend-invite-${emp.id}`}
                      >
                        <RotateCw className={cn("h-3 w-3", resendingInvite === emp.id && "animate-spin")} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("h-6 w-6", emailLogUserId === emp.id ? "text-orange-400" : "text-zinc-500 hover:text-zinc-300")}
                        onClick={(e) => { e.stopPropagation(); handleShowEmailLog(emp.id); }}
                        title="Email log"
                        data-testid={`email-log-${emp.id}`}
                      >
                        <Clock className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-600">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="space-y-0.5 text-xs text-zinc-400">
                    <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{emp.email}</div>
                    {emp.telegram && <div className="flex items-center gap-1"><Send className="h-3 w-3" />{emp.telegram}</div>}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  {getPaymentIcon(emp.payment_methods?.preferred_method)}
                </TableCell>
                <TableCell className="text-right">
                  <span className="font-mono text-zinc-50">{formatAmount(emp.salary, emp.currency)}</span>
                  {emp.currency !== displayCurrency && (
                    <span className="text-xs text-zinc-500 ml-1">({emp.currency})</span>
                  )}
                  {(emp.raise_amount || emp.raise_date) && (
                    <div className="text-[11px] text-emerald-400/80 font-mono mt-0.5">
                      ↑{emp.raise_amount ? formatAmount(emp.raise_amount, emp.currency) : ""}
                      {emp.raise_date ? <span className="text-zinc-500 ml-1">{emp.raise_date}</span> : null}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(emp)} className="h-8 w-8 text-zinc-500 hover:text-zinc-50">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {emp.is_active && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500 hover:text-red-400">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-zinc-50">Deactivate?</AlertDialogTitle>
                            <AlertDialogDescription className="text-zinc-400">
                              {emp.name} will be marked as inactive.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-zinc-700">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(emp.id)} className="bg-red-600 hover:bg-red-700">
                              Deactivate
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
              {/* Email Log Panel */}
              {emailLogUserId === emp.id && (
                <TableRow className="border-zinc-800 bg-zinc-900/80">
                  <TableCell colSpan={8} className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="h-3.5 w-3.5 text-zinc-400" />
                      <span className="text-xs text-zinc-400 font-medium">Email History — {emp.name}</span>
                    </div>
                    {emailLogs.length === 0 ? (
                      <p className="text-xs text-zinc-600 py-2">Нет записей</p>
                    ) : (
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {emailLogs.map((log) => (
                          <div key={log.id} className="flex items-center justify-between text-[11px] px-2.5 py-1.5 rounded bg-zinc-800/60">
                            <div className="flex items-center gap-2">
                              <span className={cn("inline-block w-1.5 h-1.5 rounded-full shrink-0",
                                log.status === "sent" && "bg-emerald-400",
                                log.status === "failed" && "bg-red-400",
                                log.status === "skipped" && "bg-yellow-400",
                              )} />
                              <span className="text-zinc-300">{log.subject}</span>
                              <span className="text-zinc-600">→ {log.to_email}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={cn("font-medium",
                                log.status === "sent" ? "text-emerald-400" : log.status === "failed" ? "text-red-400" : "text-yellow-400"
                              )}>{log.status}</span>
                              <span className="text-zinc-600 font-mono">{log.sent_at?.slice(0, 16).replace("T", " ")}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {emailLogs.some(l => l.error) && (
                      <p className="text-[10px] text-red-400/70 mt-1.5">Error: {emailLogs.find(l => l.error)?.error}</p>
                    )}
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            ))}
            {/* Total Row */}
            <TableRow className="border-zinc-800 bg-zinc-800/30 font-semibold">
              <TableCell className="text-zinc-400"></TableCell>
              <TableCell colSpan={5} className="text-zinc-300 text-right">
                Total Salary ({filteredEmployees.filter(e => e.is_active).length} active employees):
              </TableCell>
              <TableCell className="text-right">
                <span className="font-mono text-orange-400 text-lg">
                  {getCurrencySymbol()}{filteredEmployees.filter(e => e.is_active).reduce((sum, e) => {
                    return sum + convertAmount(e.salary || 0, e.currency || "UAH");
                  }, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default StaffPage;
